import { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, Marker, Circle } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../utils/googleMaps';
import { toast } from 'sonner';
import { Navigation, MapPin } from 'lucide-react';
import shp from 'shpjs';

const MapFieldDrawer = ({ onFieldCreated, onCancel }) => {
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 }); // Default NYC
  const [markers, setMarkers] = useState([]);
  const [isFollowMode, setIsFollowMode] = useState(false);
  const [uploadedPolygon, setUploadedPolygon] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const fileInputRef = useRef(null);
  const watchIdRef = useRef(null);
  const locationWatchRef = useRef(null);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);

  const [map, setMap] = useState(null);

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Start watching user location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      locationWatchRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setUserLocation(pos);
          setLocationError(null);
        },
        (error) => {
          console.log('Location error:', error.message);
          if (error.code === 1) {
            setLocationError('Location permission denied');
          } else if (error.code === 2) {
            setLocationError('Location unavailable');
          } else {
            setLocationError('Location timeout');
          }
        },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );
    }
    
    return () => {
      if (locationWatchRef.current) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
      }
    };
  }, []);

  // Get user's location and center map
  const handleMyLocation = () => {
    if (userLocation) {
      setMapCenter({ lat: userLocation.lat, lng: userLocation.lng });
      if (map) {
        map.panTo({ lat: userLocation.lat, lng: userLocation.lng });
        map.setZoom(17);
      }
      toast.success('Centered on your location');
    } else if (locationError) {
      toast.error(locationError);
    } else {
      toast.info('Getting location...');
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            setMapCenter(pos);
            setUserLocation(pos);
            if (map) {
              map.panTo(pos);
              map.setZoom(17);
            }
            toast.success('Location found!');
          },
          (error) => {
            if (error.code === 1) {
              toast.error('Location permission denied. Please enable in browser settings.');
            } else {
              toast.error('Could not get location');
            }
          }
        );
      } else {
        toast.error('Geolocation not supported');
      }
    }
  };

  // Follow me mode - track user location
  const handleFollowMode = () => {
    if (!isFollowMode) {
      if (navigator.geolocation) {
        const watchId = navigator.geolocation.watchPosition(
          (position) => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            setMapCenter(pos);
            setUserLocation({ ...pos, accuracy: position.coords.accuracy });
            if (map) {
              map.panTo(pos);
            }
          },
          (error) => {
            if (error.code === 1) {
              toast.error('Location permission denied');
            } else {
              toast.error('Error tracking location');
            }
            console.error(error);
          },
          { enableHighAccuracy: true, maximumAge: 1000 }
        );
        watchIdRef.current = watchId;
        setIsFollowMode(true);
        toast.success('Follow mode active - walk around field boundary');
      }
    } else {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      setIsFollowMode(false);
      toast.info('Follow mode stopped');
    }
  };

  // Add marker at current location
  const handleAddMarker = () => {
    if (userLocation) {
      const newMarker = {
        lat: userLocation.lat,
        lng: userLocation.lng
      };
      setMarkers([...markers, newMarker]);
      toast.success(`Marker ${markers.length + 1} added`);
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newMarker = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setMarkers([...markers, newMarker]);
          toast.success(`Marker ${markers.length + 1} added`);
        },
        () => {
          toast.error('Could not get current location');
        }
      );
    } else {
      toast.error('Geolocation not supported');
    }
  };

  // Add marker by clicking on map
  const handleMapClick = (e) => {
    if (!isFollowMode) {
      const newMarker = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      };
      setMarkers([...markers, newMarker]);
      toast.success(`Marker ${markers.length + 1} added`);
    }
  };

  // Clear all markers
  const handleClear = () => {
    setMarkers([]);
    setUploadedPolygon(null);
    toast.info('Markers cleared');
  };

  // Upload shapefile or KML
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();

    if (fileName.endsWith('.kml')) {
      // Parse KML
      const text = await file.text();
      const parser = new DOMParser();
      const kml = parser.parseFromString(text, 'text/xml');
      
      // Extract coordinates from KML
      const coordinates = kml.getElementsByTagName('coordinates')[0];
      if (coordinates) {
        const coordText = coordinates.textContent.trim();
        const coords = coordText.split(/\s+/).map(coord => {
          const [lng, lat] = coord.split(',').map(Number);
          return { lat, lng };
        });
        
        setMarkers(coords);
        setUploadedPolygon(coords);
        
        // Center map on polygon
        if (coords.length > 0 && map) {
          map.panTo(coords[0]);
          map.setZoom(15);
        }
        
        toast.success('KML file loaded successfully!');
      } else {
        toast.error('No coordinates found in KML file');
      }
    } else if (fileName.endsWith('.shp') || fileName.endsWith('.zip')) {
      // Parse Shapefile (must be zipped with .shp, .shx, .dbf)
      try {
        const arrayBuffer = await file.arrayBuffer();
        const geojson = await shp(arrayBuffer);
        
        // Extract first polygon
        const feature = geojson.features ? geojson.features[0] : geojson;
        if (feature.geometry && feature.geometry.type === 'Polygon') {
          const coords = feature.geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
          
          setMarkers(coords);
          setUploadedPolygon(coords);
          
          // Center map on polygon
          if (coords.length > 0 && map) {
            map.panTo(coords[0]);
            map.setZoom(15);
          }
          
          toast.success('Shapefile loaded successfully!');
        } else {
          toast.error('No polygon found in shapefile');
        }
      } catch (error) {
        toast.error('Error parsing shapefile: ' + error.message);
      }
    } else {
      toast.error('Please upload a .kml or .shp/.zip file');
    }
  };

  // Save field
  const handleSave = () => {
    if (markers.length < 3) {
      toast.error('Please add at least 3 markers to create a field boundary');
      return;
    }

    // Convert to GeoJSON polygon format (lng, lat)
    const coordinates = markers.map(m => [m.lng, m.lat]);
    // Close the polygon
    coordinates.push(coordinates[0]);

    const geometry = {
      type: 'Polygon',
      coordinates: [coordinates]
    };

    onFieldCreated(geometry);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (locationWatchRef.current) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
      }
    };
  }, []);

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[rgb(var(--primary))] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-[85vh] overflow-hidden">
      {/* Map Controls - Scrollable on mobile */}
      <div className="flex-shrink-0 overflow-x-auto pb-2 mb-2">
        <div className="flex gap-2 min-w-max px-1">
          <button
            onClick={handleMyLocation}
            className="btn-depth-primary text-xs sm:text-sm whitespace-nowrap py-2 px-3"
            data-testid="my-location-button"
          >
            Location
          </button>
          
          <button
            onClick={handleFollowMode}
            className={`btn-depth-secondary text-xs sm:text-sm px-3 py-2 whitespace-nowrap`}
            style={{
              background: isFollowMode ? 'var(--danger)' : undefined,
              borderColor: isFollowMode ? 'var(--danger)' : undefined
            }}
            data-testid="follow-mode-button"
          >
            {isFollowMode ? 'Stop' : 'Follow'}
          </button>
          
          <button
            onClick={handleAddMarker}
            className="btn-depth-secondary text-xs sm:text-sm whitespace-nowrap py-2 px-3"
            data-testid="add-marker-button"
          >
            + Marker ({markers.length})
          </button>
          
          <button
            onClick={handleClear}
            className="btn-depth-ghost text-xs sm:text-sm px-3 py-2 whitespace-nowrap"
          >
            Clear
          </button>
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-depth-secondary text-xs sm:text-sm whitespace-nowrap py-2 px-3"
          >
            Upload
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".kml,.shp,.zip"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      </div>

      {/* Map - Flexible height with minimum size */}
      <div className="flex-1 min-h-[250px] border rounded-lg overflow-hidden bg-gray-100 relative">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%', minHeight: '250px' }}
          center={mapCenter}
          zoom={13}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          options={{
            mapTypeId: 'satellite',
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: false
          }}
        >
          {/* User location blue dot */}
          {userLocation && (
            <>
              {/* Accuracy circle */}
              <Circle
                center={{ lat: userLocation.lat, lng: userLocation.lng }}
                radius={userLocation.accuracy || 20}
                options={{
                  fillColor: '#4285F4',
                  fillOpacity: 0.15,
                  strokeColor: '#4285F4',
                  strokeOpacity: 0.3,
                  strokeWeight: 1,
                  zIndex: 1
                }}
              />
              {/* Blue dot */}
              <Circle
                center={{ lat: userLocation.lat, lng: userLocation.lng }}
                radius={8}
                options={{
                  fillColor: '#4285F4',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeOpacity: 1,
                  strokeWeight: 3,
                  zIndex: 2
                }}
              />
            </>
          )}

          {/* Field boundary markers */}
          {markers.map((marker, index) => (
            <Marker
              key={index}
              position={marker}
              label={`${index + 1}`}
            />
          ))}

          {/* Polygon */}
          {markers.length >= 3 && (
            <Polygon
              paths={markers}
              options={{
                fillColor: '#22c55e',
                fillOpacity: 0.3,
                strokeColor: '#16a34a',
                strokeOpacity: 0.8,
                strokeWeight: 2,
              }}
            />
          )}
        </GoogleMap>

        {/* Floating My Location button */}
        <button
          onClick={handleMyLocation}
          className="absolute bottom-4 right-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 z-10"
          title="My Location"
          data-testid="floating-location-button"
        >
          <Navigation className="w-5 h-5 text-blue-600" />
        </button>
      </div>

      {/* Instructions - Collapsible on mobile */}
      <details className="flex-shrink-0 mt-2 card-floating text-sm">
        <summary className="p-3 cursor-pointer font-medium" style={{ color: 'var(--text)' }}>Instructions (tap to expand)</summary>
        <ul className="list-disc list-inside space-y-1 p-3 pt-0" style={{ color: 'var(--text-secondary)' }}>
          <li><strong style={{ color: 'var(--text)' }}>Click on map</strong> to add markers manually</li>
          <li><strong style={{ color: 'var(--text)' }}>Location:</strong> Center map on your current position</li>
          <li><strong style={{ color: 'var(--text)' }}>Follow:</strong> Track your location as you walk around the field</li>
          <li><strong style={{ color: 'var(--text)' }}>Marker:</strong> Add a marker at your current GPS location</li>
          <li><strong style={{ color: 'var(--text)' }}>Upload:</strong> Import field boundary from .kml or .shp file</li>
          <li>Minimum 3 markers required to create a field</li>
        </ul>
      </details>

      {/* Action Buttons - Always visible at bottom */}
      <div className="flex-shrink-0 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-soft)' }}>
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={markers.length < 3}
            className="btn-depth-primary flex-1 text-sm sm:text-base py-3"
            style={{ opacity: markers.length < 3 ? 0.5 : 1 }}
            data-testid="save-field-button"
          >
            Save ({markers.length} points)
          </button>
          <button
            onClick={onCancel}
            className="btn-depth-secondary flex-1 text-sm sm:text-base py-3"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapFieldDrawer;
